/**
 * 海报来源条的**数据契约**(V1 §3.4)—— 6 条来源、每条各自带什么、能做什么。
 *
 * 契约与渲染分开放:渲染在 `ui/components/PosterSourceBar.tsx`,判据(各页面怎么从自己的
 * state 推出 kind)在各页面。放这里的好处是各页面不必 import 组件文件才能标注类型,
 * 单测也能按 `POSTER_SOURCE_KINDS` 做全覆盖(新增一条忘了写样例就红)。
 *
 * ★`template` / `product` 两条本波先落契约与渲染,挂载点分别在波A(模板迁长图)、
 *  波B(产品树进商品出图)接上 —— 先把形制定死,免得下一波又长出第五种壳。
 */

/** 全部来源 kind —— 单测按这张表做全覆盖。 */
export const POSTER_SOURCE_KINDS = [
  "free",
  "scene",
  "template",
  "explosive",
  "plan",
  "product",
] as const;

export type PosterSourceKind = (typeof POSTER_SOURCE_KINDS)[number];

export type PosterSourceBarData =
  /**
   * 直接进页。`onPickPurpose` = 去卡墙挑目的;**缺省则不渲染那个按钮**。
   * ★创建页只在表单还空着时才传:它没有页内目的选择器,这个动作必然跳去舱内页,
   * 而创建页的草稿是手动保存的,用户写了文案再跳走就白写了(视频侧 codex 第五轮的同型缺陷)。
   * 搭建器草稿走 localStorage 自动保存,跳走再回来能恢复,故恒传。
   */
  | { kind: "free"; onPickPurpose?: () => void }
  /** 点场景卡进来。`onClear` 缺省 = 这条路径退不回去(见 PosterSourceBar 文件头)。 */
  | { kind: "scene"; sceneKey: string | null; onClear?: () => void }
  /** 长图模板库(波A 接上)。 */
  | {
      kind: "template";
      name: string;
      coverUrl?: string | null;
      /** 模板预填了几个区块 —— 长图模板与海报模板最大的差别,不说等于让用户盲选。 */
      blockCount?: number;
      /** 模板是否带配色(L2)。 */
      withTokens?: boolean;
      onChange: () => void;
    }
  /**
   * 追爆蓝图 / 组图。
   * `editable` 非空 = 这条来源还能换/退(创建页组图)→ 嵌蓝图工作卡(`BlueprintHeroCard`,
   *   BPWB-1 2026-09-03 起,原 `PosterBlueprintBar` 已退役);
   * `editable` 为 null = 爆点已物化成区块(搭建器)→ 只说明来路,不给第二份真相。
   *
   * ★2026-07-28:爆点本体(points/draft)不再走这里 —— 它搬进主区「说什么」段的
   *   `GroupPointsEditor`。来源条曾经既说来路又收内容,导致爆点和创作描述隔了 900px。
   */
  | {
      kind: "explosive";
      formulaName: string | null;
      sourcePlatform?: string | null;
      /** 蓝图 payload —— 只为「这次带给 AI 的原文」面板(2026-08-13);缺省则不渲染那块。 */
      payload?: import("@/entities/explosive").FormulaPayload | null;
      /** 2026-08-14「参考片在拍什么」:内容层文本(已装配好);缺省则不渲染那块。 */
      referenceContent?: string | null;
      /**
       * BPWB-1(2026-09-03)蓝图工作卡:原片播放位的判据(与视频/口播/官媒三条同名同义)。
       * `blueprintId` 缺省(无蓝图的手动组图态)→ 走极简 banner,不进 `BlueprintHeroCard`
       * (那句「已套用蓝图」在没有蓝图时是假话)。
       */
      blueprintId?: number | null;
      hasRawVideo?: boolean;
      editable: {
        onSwap: () => void;
        onClear: () => void;
      } | null;
      /** `editable === null` 时说明注入了几块。 */
      injectedBlocks?: number;
      /**
       * 搭建器编排态的来路操作(2026-08-10 D1:长图从蓝图进来后原来看不出来路、也换不掉)。
       * 与 `editable`(爆点可编辑)无关 —— 爆点物化成区块后仍要能「换蓝图/清除从零」。
       */
      actions?: { onSwap: () => void; onClear: () => void } | null;
    }
  /** AI 智能规划稿(搭建器 Canvas 模式)。 */
  | { kind: "plan"; productName?: string | null; blockCount: number; onDiscard: () => void }
  /** 产品库直选(波B 接上)。 */
  | { kind: "product"; productName: string; onChange: () => void };

/* ══════════════════════════════════════════════════════════════════════════════
 * 来源**判定**(与上面的展示契约分开)
 *
 * 形制照抄视频侧 `model/videoSource.ts`:把「我从哪来」判一次成纯函数,页面**正面读
 * 字段**而不是层层否定式 if。海报侧改之前正是那个样子 —— 创建页两条 bar 各自一个独立
 * 的 if(两条可能同时渲染),搭建器四处各判各的。判据散着的表现不是报错,而是两条来源
 * 同时出现、或该出的没出,只有真跑那条来源才看得见。
 *
 * ★描述符只出**纯数据**(kind + 两个开关),回调留在页面里 —— 这样判定能被真值表逐档
 *   锁死(`posterSource.test.ts`),而回调不进测试的可比较面。
 * ★两条管线各一个 resolver,不合成一个:单帧与长图的来源集合本来就不同
 *   (长图有智能规划稿,单帧有组图),硬合会做出一个到处 if 的巨函数。
 * ══════════════════════════════════════════════════════════════════════════════ */

export interface PosterSourceUi {
  kind: PosterSourceKind;
  /**
   * `scene` 来源给不给「退出」。
   * 创建页给:场景效果(组图模式 / 套图画幅)是可复位的纯 state。
   * 搭建器不给:已按用途把区块铺好,退不回「没有区块」的状态,给了就是假承诺。
   */
  canExitScene: boolean;
  /**
   * `explosive` 的爆点还能不能改。
   * 创建页组图能(原样嵌蓝图工作卡);搭建器不能 —— 爆点已物化成区块,
   * 用户改的是区块不是爆点,再给爆点编辑器就是两份真相。
   */
  blueprintEditable: boolean;
}

export interface PosterCreateSourceInput {
  /** 图改图模式(I2I 没有组图)。 */
  isI2I: boolean;
  /** 组图态已开(`form.groupPoints !== null`)—— 蓝图预载与手填组图都落这里。 */
  groupModeActive: boolean;
  /** 点场景卡进来时的打标(`form.sceneKey`)。 */
  sceneKey: string | null;
  /** 波B:从产品库直选了一个产品(`form.product !== null`)。 */
  productPicked: boolean;
}

/**
 * 单帧创建页:**组图/蓝图 > 产品库 > 场景卡 > 自由**。
 *
 * 组图态压过场景卡不是「压掉信息」——「爆点组图」那张卡承诺的**就是**组图模式,
 * 两条一起显会把同一件事说两遍;而组图那条还多带爆点编辑器,信息量严格更大。
 * 退出组图后场景卡那条会自己回来(`sceneKey` 还在),交代得上。
 *
 * ★波B 把产品库排在场景卡之前,判据是**哪一条真的决定了这张图的内容**:
 * 产品库直选把名称/卖点/参数逐字送进出图指令(它替你定了画面上会出现什么);
 * 场景卡只决定进页时的默认参数(画幅/张数/组图态),内容仍然是用户自己写的。
 * 打标不受影响 —— `sceneKey` 照旧随提交上送,只是同一个位置不说两遍。
 */
export function resolvePosterCreateSource(input: PosterCreateSourceInput): PosterSourceUi {
  const { isI2I, groupModeActive, sceneKey, productPicked } = input;
  if (!isI2I && groupModeActive) {
    return { kind: "explosive", canExitScene: false, blueprintEditable: true };
  }
  if (productPicked) return { kind: "product", canExitScene: false, blueprintEditable: false };
  if (sceneKey) return { kind: "scene", canExitScene: true, blueprintEditable: false };
  return { kind: "free", canExitScene: false, blueprintEditable: false };
}

export interface PosterLongImageSourceInput {
  /** AI 智能规划稿已激活(`useCanvasBuilder().active`)。 */
  canvasMode: boolean;
  /** 追爆蓝图 handoff 在场(进页时一次性验型的那份)。 */
  hasHandoff: boolean;
  /** 波A:长图模板整包预填在场(唯一验型器消费过的那份)。 */
  hasTemplate: boolean;
  /** 用途卡 / 横宽卡带进来的打标。 */
  sceneKey: string | null;
}

/**
 * 长图搭建器:**智能稿 > 蓝图 > 模板 > 场景卡 > 自由**。
 *
 * 顺序与页面真实的覆盖关系一致:激活智能稿会盖掉手动区块;蓝图 handoff 优先于模板、
 * 模板优先于卡片 preset(与 `useLongImageBuilder` 的 seeding 优先级同一份口径,
 * 波A 定案 B3:explosive > template > scenePreset > 草稿)。
 */
export function resolveLongImageSource(input: PosterLongImageSourceInput): PosterSourceUi {
  const { canvasMode, hasHandoff, hasTemplate, sceneKey } = input;
  if (canvasMode) return { kind: "plan", canExitScene: false, blueprintEditable: false };
  if (hasHandoff) return { kind: "explosive", canExitScene: false, blueprintEditable: false };
  if (hasTemplate) return { kind: "template", canExitScene: false, blueprintEditable: false };
  if (sceneKey) return { kind: "scene", canExitScene: false, blueprintEditable: false };
  return { kind: "free", canExitScene: false, blueprintEditable: false };
}

/**
 * 这条来源要不要占**页顶**那条独立的来源条(2026-07-31 版面收敛)。
 *
 * 判据不是「这个来源重不重要」,而是:**页面别处是不是已经在说同一件事,而且说得离操作更近**。
 * 页顶那条 strip 高 79px 全宽,只有当它是该信息的**唯一出口**时才配占这个位置。
 *
 * ★`free`(没有任何来源)—— 三句话里两句在别处逐字重复:页面副标题已写
 *   「用文字描述,或上传一张图来改」、大框标题下已写「写一段话就行」。
 *   唯一有增量的是「挑个目的」这个**动作**:动作留下(并进次要动作条),壳去掉。
 *
 * ★`product`(选了产品)—— **位置错得更离谱**。选产品的入口是大框内那个 chip,
 *   而这条反馈弹在页顶:2026-07-31 本机实测,用户滚到 scrollY=283 点 chip 选完产品,
 *   strip 的 y 坐标是 **−85** —— 字面意义上不在屏幕上,用户看不见。
 *   而同一时刻那个 chip 自己已经变成「已选 经典美式」并染主色,就在他手指底下。
 *   反馈必须落在触发它的地方;弹到视口外的那份不是"多一层确认",是噪音。
 *   换/清产品也没丢:chip 点开抽屉里就有「清除」。
 *
 * ★其余 4 个(scene / explosive / template / plan)**要**:它们是从别的页带进来的
 *   上下文,页内没有第二个地方在说「我从哪来、它替我定了什么、怎么换」。
 *
 * 抽成谓词而不是在组件里内联判断:判据要可测、且只有一个真相。
 */
export function shouldRenderSourceBar(kind: PosterSourceKind): boolean {
  return kind !== "free" && kind !== "product";
}

/**
 * 创建页要不要给「挑个目的」这个入口(2026-07-31)。
 *
 * ★**图改图不给**。`resolvePosterCreateSource` 在 i2i 且没选产品/场景卡时同样返回 `free`,
 *   这个入口于是跟着长在了改图那条路上。两件事都不对:
 *
 *   ① **代价不对称** —— 点它要离开创建页(本页没有页内目的选择器),而改图态下用户
 *      已经传了参考图、可能还涂了 mask、写了改图指令,一走全没。文字态那边此时通常
 *      还没投入什么。
 *   ② **去处也不对** —— 卡墙上那些"目的"卡(商品主图 / 爆点组图 / 多比例套图…)承诺的
 *      全是**从零创作**的 T2I 形态,挑完回来仍然不是「改这张图」。
 *      给一个点进去解决不了他问题的入口,比不给更糟。
 *
 * 抽成谓词的理由同 `shouldRenderSourceBar`:「这个动作存不存在」只决定一次,
 * `PosterSourceBar` 与页内动作条都按 `onPickPurpose` 在不在来渲染,两边自动一致。
 */
export function shouldOfferPickPurpose(isI2I: boolean): boolean {
  return !isI2I;
}
